import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Share2, MessageSquare, Mail, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareAmendmentOffer — a compact card that lets anyone share a $175
 * amendment offer via SMS, email, or copy link. The URL includes an
 * optional `?ref=<email-or-phone>` param so the admin dashboard can see
 * who's driving referrals.
 */
export default function ShareAmendmentOffer({ compact = false }) {
  const [ref, setRef] = useState("");

  const buildLink = () => {
    const base = `${window.location.origin}/amend`;
    const clean = (ref || "").trim();
    return clean ? `${base}?ref=${encodeURIComponent(clean)}` : base;
  };

  const message = () =>
    `Missed a credit or need to fix a prior tax return? DyNamic Tax is doing 1040-X amendments for $175 through Oct 15 (jumps to $275 after). One flat fee, we do the work: ${buildLink()}`;

  const shareSMS = () => {
    const body = encodeURIComponent(message());
    // iOS uses `sms:&body=`; Android uses `sms:?body=` — most modern OSes handle both.
    window.location.href = `sms:?&body=${body}`;
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("Fix a prior tax return for $175 (before Oct 15)");
    const body = encodeURIComponent(message());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildLink());
      toast.success("Link copied — paste it wherever your people hang out.");
    } catch {
      toast.error("Copy failed — highlight the link above and copy manually.");
    }
  };

  return (
    <Card className={`p-6 border-2 border-primary/30 bg-primary/5 ${compact ? "" : "mt-6"}`} data-testid="share-amendment-card">
      <div className="flex items-center gap-2 mb-2">
        <Share2 size={16} className="text-primary" />
        <div className="label-caps text-[10px] text-primary">Refer a friend</div>
      </div>
      <div className="font-serif text-xl mb-1 leading-tight">Send someone the $175 offer.</div>
      <p className="text-sm text-muted-foreground mb-4">
        Know someone with a corrected W-2 or a missed credit? Text or email them this offer before it jumps to $275 on Oct 15.
      </p>

      <div className="mb-4">
        <Label className="label-caps text-[10px]">Your email or phone (optional — for tracking)</Label>
        <Input
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="you@example.com or 555-123-4567"
          className="mt-1 font-mono text-xs"
          data-testid="share-amendment-ref"
        />
      </div>

      <div className="rounded-md bg-background border border-border p-3 mb-4">
        <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Share link</div>
        <div className="font-mono text-xs break-all text-foreground" data-testid="share-amendment-link">
          {buildLink()}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Button onClick={shareSMS} variant="outline" data-testid="share-amendment-sms">
          <MessageSquare size={14} className="mr-1" /> Text
        </Button>
        <Button onClick={shareEmail} variant="outline" data-testid="share-amendment-email">
          <Mail size={14} className="mr-1" /> Email
        </Button>
        <Button onClick={copyLink} data-testid="share-amendment-copy">
          <Copy size={14} className="mr-1" /> Copy
        </Button>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-3">
        <CheckCircle2 size={11} className="text-primary" />
        <span>Anyone who books through your link is auto-tagged in the admin dashboard.</span>
      </div>
    </Card>
  );
}
